import { links, Request } from '@app/client';
import {
    type AddToWishlistParam,
    type CreateWishlistParam,
    type CreateWishlistResponse,
    type EditWishlistParam,
    type FollowWishlistParam,
    type ItemType,
    type IWishlist,
    type WishlistPostParam,
    type WishlistPostResponse,
} from '@app/client/common/structs/wishlists/wishlists';

import { apiReturnPaginatedResults, apiReturnResults } from '../../apiFactory/apiReturn';
import {
    type ApiResult,
    type BaseParams,
    type CompetitionWishlistItem,
    type PaginatedResult,
    type PatchParams,
    type PostParams,
    type ResourceId,
} from '../../common';

import {
    type WishlistItem,
    type WishListItemSearchResponse,
    type WishlistMetadataResponse,
    type WishlistSummaryResponse,
} from './types';

interface CreateWishlistPostParam {
    name: string;
    private: boolean;
}
export async function getCurrentUsersWishlists(
    params: BaseParams & { slug?: string },
): Promise<ApiResult<PaginatedResult<IWishlist>>> {
    return Request.getRequest({
        ...params,
        target: params.slug ? links.wishlists.single(params.slug).wishlist() : links.wishlists.collection,
    });
}

export async function getUsersWishlists(
    params: BaseParams & { userId: ResourceId },
): Promise<ApiResult<PaginatedResult<IWishlist>>> {
    return Request.getRequest({
        ...params,
        target: links.wishlists.userCollection(params.userId),
    });
}

export async function getWishlists(params: BaseParams): Promise<ApiResult<PaginatedResult<IWishlist>>> {
    const target = params.query?.public ? `${links.wishlists.collection}/public` : links.wishlists.collection;
    return Request.getRequest({
        ...params,
        target,
    });
}
export async function addToUserWishlist(
    params: BaseParams & PostParams<WishlistPostParam> & { slug: ResourceId },
): Promise<ApiResult<WishlistPostResponse>> {
    return Request.postRequest({
        ...params,
        target: links.wishlists.single(params.slug).items(),
    });
}

export async function createUserWishlist(
    params: BaseParams & PostParams<CreateWishlistPostParam>,
): Promise<ApiResult<CreateWishlistResponse>> {
    return Request.postRequest({
        ...params,
        target: links.wishlists.collection,
    });
}

export async function updateUserWishlist(
    params: BaseParams & PatchParams<CreateWishlistPostParam> & { wishlist_id: string },
): Promise<ApiResult<CreateWishlistResponse>> {
    return Request.patchRequest({
        ...params,
        target: links.wishlists.single(params.wishlist_id).wishlist(),
    });
}

export async function deleteFromUserWishlist(
    params: BaseParams & PostParams<WishlistPostParam> & { slug: ResourceId },
): Promise<ApiResult<WishlistPostResponse>> {
    return Request.deleteRequest({
        ...params,
        target: links.wishlists.single(params.slug).items(),
    });
}
export async function deleteUserWishlist(
    params: BaseParams & { wishlist_id: ResourceId },
): Promise<ApiResult<WishlistMetadataResponse>> {
    return Request.deleteRequest({
        ...params,
        target: links.wishlists.single(params.wishlist_id).wishlist(),
    });
}

export async function getItemInWishlists(
    params: BaseParams & { key: string },
): Promise<ApiResult<WishListItemSearchResponse>> {
    return Request.getRequest({
        ...params,
        target: links.wishlists.itemsByKey(params.key),
    });
}

export async function getWishlistMetadata(
    params: BaseParams & { slug: ResourceId },
): Promise<ApiResult<WishlistMetadataResponse>> {
    return Request.getRequest({
        ...params,
        target: links.wishlists.single(params.slug).wishlist(),
    });
}

export async function getWishlistItems(
    params: BaseParams & { wishlist_id: ResourceId; type: ItemType },
): Promise<ApiResult<PaginatedResult<WishlistItem>>> {
    return Request.getRequest({
        ...params,
        target: links.wishlists.single(params.wishlist_id).itemsByType(params.type),
    });
}

export async function getWishlistsSummary(
    params: BaseParams & { slug: ResourceId; compareDate?: string },
): Promise<ApiResult<WishlistSummaryResponse>> {
    return Request.getRequest({
        ...params,
        target: links.wishlists.single(params.slug).summary(),
    });
}

export async function followWishlist(
    params: BaseParams & PostParams & FollowWishlistParam,
): Promise<ApiResult<WishlistPostResponse>> {
    return Request.postRequest({
        ...params,
        target: links.wishlists.single(params.slug).follow(),
    });
}

export async function unfollowWishlist(
    params: BaseParams & PostParams & FollowWishlistParam,
): Promise<ApiResult<WishlistPostResponse>> {
    return Request.deleteRequest({
        ...params,
        target: links.wishlists.single(params.slug).follow(),
    });
}

export async function getFantasyWishlists(
    params: BaseParams,
): Promise<ApiResult<PaginatedResult<CompetitionWishlistItem>>> {
    return Request.getRequest({
        ...params,
        query: { wishlist_type: 'Fantasy' },
        target: links.wishlists.collection,
    });
}

export async function deleteFromUserLineup(
    params: BaseParams & PostParams<WishlistPostParam> & { wishlistId: ResourceId },
): Promise<ApiResult<WishlistPostResponse>> {
    return Request.deleteRequest({
        ...params,
        target: links.fantasy.lineupItems(params.wishlistId),
    });
}

export async function getLineupSummary(
    params: BaseParams & { slug: ResourceId },
): Promise<ApiResult<WishlistSummaryResponse>> {
    return Request.getRequest({
        ...params,
        target: links.fantasy.summary(params.slug),
    });
}
export const callUnfollowWishlist = (params: BaseParams & FollowWishlistParam) =>
    apiReturnResults<WishlistPostResponse>(unfollowWishlist, params);
export const callGetFantasyWishlists = (params: BaseParams) =>
    apiReturnPaginatedResults<CompetitionWishlistItem>(getFantasyWishlists, params);

export const callDeleteFromUserLineup = ({ wishlistId, value }: AddToWishlistParam) =>
    apiReturnResults<WishlistPostResponse>(deleteFromUserLineup, {
        wishlistId,
        value,
    });
export const callGetWishlistItems = (params: BaseParams & { wishlist_id: ResourceId; type: ItemType }) =>
    apiReturnPaginatedResults<WishlistItem>(getWishlistItems, params);
export const callGetWishlistMetadata = (params: BaseParams & { slug: ResourceId }) =>
    apiReturnResults<WishlistMetadataResponse>(getWishlistMetadata, params);

export const callDeleteUserWishlist = (params: BaseParams & { wishlist_id: ResourceId }) =>
    apiReturnResults<WishlistMetadataResponse>(deleteUserWishlist, params);

export const callItemInWishlists = (params: BaseParams & { key: string }) =>
    apiReturnResults<WishListItemSearchResponse>(getItemInWishlists, params);

export const callGetWishlistsSummary = (params: BaseParams & { slug: ResourceId; compareDate?: string }) =>
    apiReturnResults<WishlistSummaryResponse>(getWishlistsSummary, params);
export const callFollowWishlist = (params: BaseParams & FollowWishlistParam) =>
    apiReturnResults<WishlistPostResponse>(followWishlist, params);
export const callAddToUserWishlist = ({ wishlistId, value }: AddToWishlistParam) =>
    apiReturnResults<WishlistPostResponse>(addToUserWishlist, {
        slug: wishlistId,
        value,
    });

export const callCreateUserWishlist = (params: BaseParams & CreateWishlistParam) =>
    apiReturnResults<CreateWishlistResponse>(createUserWishlist, {
        ...params,
        value: {
            name: params.name,
            private: params.isPrivate,
            wishlist_type: params.wishlist_type,
        },
    });

export const callUpdateUserWishlist = (params: BaseParams & EditWishlistParam) =>
    apiReturnResults<CreateWishlistResponse>(updateUserWishlist, {
        ...params,
        value: {
            name: params.name,
            private: params.isPrivate,
        },
    });

export const callDeleteFromUserWishlist = ({ wishlistId, value }: AddToWishlistParam) =>
    apiReturnResults<WishlistPostResponse>(deleteFromUserWishlist, {
        slug: wishlistId,
        value,
    });

export const callGetLineupSummary = (params: BaseParams & { slug: ResourceId }) =>
    apiReturnResults<WishlistSummaryResponse>(getLineupSummary, params);

export const callGetCurrentUserWishlists = (params: BaseParams & { slug?: string }) =>
    apiReturnPaginatedResults<IWishlist>(getCurrentUsersWishlists, params);

export const callGetUserWishlists = (params: BaseParams & { userId: ResourceId }) =>
    apiReturnPaginatedResults<IWishlist>(getUsersWishlists, params);

export const callGetAllWishlists = (params: BaseParams) => apiReturnPaginatedResults<IWishlist>(getWishlists, params);
